import React, {useEffect} from 'react';
import {Alert} from 'bonprix-design-system';
import {useTextsContext} from '@account/shared-components/providers/TextsProvider';
import {WITHDRAWAL_REASON, WITHDRAWAL_STATUS} from '@account/pages/withdrawal/WithdrawalTracking';
import WithdrawalTracking from '@account/pages/withdrawal/WithdrawalTracking';

export enum FormState {
    NOT_SUBMITTED = 'notSubmitted',
    ERROR = 'error',
    SUCCESS = 'success',
}

// Message ids returned (already parsed, without the numeric prefix) by the SAP backend, or
// synthesized by the BFF (INVALID_CAPTCHA).
export enum WithdrawalMessageId {
    COMBINATION_NOT_VALID = 'COMBINATION_NOT_VALID',
    TIME_EXPIRED = 'TIME_EXPIRED',
    SUCCESSFULLY_CANCELLED = 'SUCCESSFULLY_CANCELLED',
    PARTIALLY_CANCELLED = 'PARTIALLY_CANCELLED',
    SUCCESSFULLY_CANCELLED_IN_DELIVERY = 'SUCCESSFULLY_CANCELLED_IN_DELIVERY',
    ALREADY_REGISTERED = 'ALREADY_REGISTERED',
    GENERIC_ERROR = 'GENERIC_ERROR',
    ORDER_ALREADY_CANCELLED = 'ORDER_ALREADY_CANCELLED',
    INVALID_CAPTCHA = 'INVALID_CAPTCHA',
}

interface WithdrawalAlertProps {
    formState: FormState;
    messageId: WithdrawalMessageId | undefined;
    orderNumber: string;
    onClose: () => void;
}

const RESPONSE_MESSAGE_TO_REASON: Record<WithdrawalMessageId, WITHDRAWAL_REASON> = {
    [WithdrawalMessageId.COMBINATION_NOT_VALID]: 'invalidCredentials',
    [WithdrawalMessageId.TIME_EXPIRED]: 'periodExpired',
    [WithdrawalMessageId.SUCCESSFULLY_CANCELLED]: 'fullCancellation',
    [WithdrawalMessageId.PARTIALLY_CANCELLED]: 'mixedAction',
    [WithdrawalMessageId.SUCCESSFULLY_CANCELLED_IN_DELIVERY]: 'fullCancellation',
    [WithdrawalMessageId.ALREADY_REGISTERED]: 'alreadyRegistered',
    [WithdrawalMessageId.GENERIC_ERROR]: 'invalidCredentials',
    [WithdrawalMessageId.ORDER_ALREADY_CANCELLED]: 'alreadyCancelled',
    [WithdrawalMessageId.INVALID_CAPTCHA]: 'invalidCredentials',
};

// Parses the raw backend message id into a known WithdrawalMessageId (or undefined when unknown).
export const parseWithdrawalMessageId = (messageId: string | undefined): WithdrawalMessageId | undefined => {
    const normalized = (messageId ?? '').trim().toUpperCase();
    return Object.values(WithdrawalMessageId).includes(normalized as WithdrawalMessageId)
        ? (normalized as WithdrawalMessageId)
        : undefined;
};

// Only a complete, clean cancellation does not require a return from the customer.
export const isFullCancellation = (messageId: WithdrawalMessageId | undefined): boolean =>
    messageId === WithdrawalMessageId.SUCCESSFULLY_CANCELLED;

const mapMessageIdToReason = (messageId: WithdrawalMessageId | undefined): WITHDRAWAL_REASON =>
    (messageId && RESPONSE_MESSAGE_TO_REASON[messageId]) ?? 'fullCancellation';

const WithdrawalAlert = ({formState, messageId, orderNumber, onClose}: WithdrawalAlertProps) => {
    const {texts} = useTextsContext();

    useEffect(() => {
        if (formState !== FormState.NOT_SUBMITTED) {
            window.scrollTo({top: 0, behavior: 'smooth'});
            const status: WITHDRAWAL_STATUS = formState === FormState.SUCCESS ? 'success' : 'failure';
            const reason: WITHDRAWAL_REASON = mapMessageIdToReason(messageId);

            WithdrawalTracking.trackViewWithdrawResult(status, reason);
            if (status === 'failure') {
                WithdrawalTracking.trackErrorView(reason, messageId);
            }
        }
    }, [formState]);

    const replaceParamWithOrderNumber = (text: string | undefined): string => (text ?? '').replace('{0}', orderNumber);

    const formatMessage = (message: string): React.ReactElement => (
        <span style={{whiteSpace: 'pre-line'}}>{message}</span>
    );

    const composeAlertMessage = (): React.ReactElement => {
        if (formState === FormState.ERROR) {
            switch (messageId) {
                case WithdrawalMessageId.TIME_EXPIRED:
                    return formatMessage(
                        replaceParamWithOrderNumber(texts['cus__withdrawal_errorMessage_timeExpired']),
                    );
                case WithdrawalMessageId.ALREADY_REGISTERED:
                    return formatMessage(
                        replaceParamWithOrderNumber(texts['cus__withdrawal_errorMessage_alreadyRegistered']),
                    );
                case WithdrawalMessageId.ORDER_ALREADY_CANCELLED:
                    return formatMessage(
                        replaceParamWithOrderNumber(texts['cus__withdrawal_errorMessage_alreadyCancelled']),
                    );
                case WithdrawalMessageId.COMBINATION_NOT_VALID:
                    return formatMessage(texts['cus__withdrawal_errorMessage_orderNotFound']);
                case WithdrawalMessageId.INVALID_CAPTCHA:
                    return formatMessage(texts['invalid_captcha']);
                case WithdrawalMessageId.GENERIC_ERROR:
                default:
                    // Backend can return localized/raw error messages during rollout.
                    return formatMessage(texts['cus__withdrawal_errorMessage_generalError']);
            }
        }

        switch (messageId) {
            case WithdrawalMessageId.SUCCESSFULLY_CANCELLED_IN_DELIVERY:
                return <>{texts['cus__withdrawal_successMessage_orderInDelivery']}</>;
            case WithdrawalMessageId.PARTIALLY_CANCELLED:
                return <>{texts['cus__withdrawal_successMessage_partialWithdrawal']}</>;
            case WithdrawalMessageId.SUCCESSFULLY_CANCELLED:
                return (
                    <>
                        {texts['cus__withdrawal_successMessage_successful']}
                        <br />
                        {replaceParamWithOrderNumber(texts['cus__withdrawal_confirmationHeading_text_cancelled'])}
                    </>
                );
            default:
                return texts['cus__withdrawal_successMessage_successful'];
        }
    };

    return (
        <Alert
            isOpen={formState !== FormState.NOT_SUBMITTED}
            status={formState === FormState.ERROR ? 'error' : 'success'}
            onClose={onClose}
        >
            {composeAlertMessage()}
        </Alert>
    );
};

export default WithdrawalAlert;
